import { useState, useEffect } from "react";
import { MessageSquare, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/hooks/useSettings";

// ---------------------------------------------------------------------------
// Tone options
// ---------------------------------------------------------------------------

const TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "firm", label: "Firm" },
  { value: "collaborative", label: "Collaborative" },
] as const;

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

interface NegotiationSettingsForm {
  negotiation_followup_days: string;
  negotiation_max_followups: string;
  negotiation_min_interval_hours: string;
  negotiation_decline_cooldown_days: string;
  cost_of_capital_apr: string;
  default_negotiation_tone: string;
  negotiation_from_email: string;
  // Tone & Voice / global negotiation defaults
  default_target_discount_pct: string;
  default_target_days_early: string;
  negotiation_signature: string;
}

function settingsToForm(settings: {
  negotiation_followup_days?: number | null;
  negotiation_max_followups?: number | null;
  negotiation_min_interval_hours?: number | null;
  negotiation_decline_cooldown_days?: number | null;
  cost_of_capital_apr?: number | null;
  default_negotiation_tone?: string | null;
  negotiation_from_email?: string | null;
  default_target_discount_pct?: number | null;
  default_target_days_early?: number | null;
  negotiation_signature?: string | null;
}): NegotiationSettingsForm {
  return {
    negotiation_followup_days:
      settings.negotiation_followup_days != null ? String(settings.negotiation_followup_days) : "4",
    negotiation_max_followups:
      settings.negotiation_max_followups != null ? String(settings.negotiation_max_followups) : "2",
    negotiation_min_interval_hours:
      settings.negotiation_min_interval_hours != null
        ? String(settings.negotiation_min_interval_hours)
        : "24",
    negotiation_decline_cooldown_days:
      settings.negotiation_decline_cooldown_days != null
        ? String(settings.negotiation_decline_cooldown_days)
        : "30",
    cost_of_capital_apr:
      settings.cost_of_capital_apr != null ? String(settings.cost_of_capital_apr) : "12",
    default_negotiation_tone: settings.default_negotiation_tone ?? "professional",
    negotiation_from_email: settings.negotiation_from_email ?? "",
    default_target_discount_pct:
      settings.default_target_discount_pct != null
        ? String(settings.default_target_discount_pct)
        : "",
    default_target_days_early:
      settings.default_target_days_early != null ? String(settings.default_target_days_early) : "",
    negotiation_signature: settings.negotiation_signature ?? "",
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NegotiationSettings() {
  const { settings, isLoading, updateSettings } = useSettings();
  const [form, setForm] = useState<NegotiationSettingsForm>(settingsToForm({}));
  const [saving, setSaving] = useState(false);

  // Sync form when settings load
  useEffect(() => {
    if (settings) {
      setForm(settingsToForm(settings));
    }
  }, [settings]);

  function setField<K extends keyof NegotiationSettingsForm>(
    key: K,
    value: NegotiationSettingsForm[K]
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings({
        negotiation_followup_days: form.negotiation_followup_days
          ? Number(form.negotiation_followup_days)
          : null,
        negotiation_max_followups: form.negotiation_max_followups
          ? Number(form.negotiation_max_followups)
          : null,
        negotiation_min_interval_hours: form.negotiation_min_interval_hours
          ? Number(form.negotiation_min_interval_hours)
          : null,
        negotiation_decline_cooldown_days: form.negotiation_decline_cooldown_days
          ? Number(form.negotiation_decline_cooldown_days)
          : null,
        cost_of_capital_apr: form.cost_of_capital_apr ? Number(form.cost_of_capital_apr) : null,
        default_negotiation_tone: form.default_negotiation_tone || null,
        negotiation_from_email: form.negotiation_from_email.trim() || null,
        default_target_discount_pct: form.default_target_discount_pct
          ? Number(form.default_target_discount_pct)
          : null,
        default_target_days_early: form.default_target_days_early
          ? Number(form.default_target_days_early)
          : null,
        negotiation_signature: form.negotiation_signature.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="w-4 h-4" />
          Negotiation Settings
        </CardTitle>
        <CardDescription>
          Set global tone &amp; voice defaults and configure automated follow-up cadence, decline
          cooldowns, and sender address for vendor negotiations.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-5">
          {/* Cadence section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Follow-up Cadence
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="neg-followup-days">Follow-up after (days)</Label>
                <Input
                  id="neg-followup-days"
                  type="number"
                  min="1"
                  max="90"
                  step="1"
                  value={form.negotiation_followup_days}
                  onChange={(e) => setField("negotiation_followup_days", e.target.value)}
                  placeholder="4"
                />
                <p className="text-xs text-muted-foreground">
                  Days of vendor silence before a follow-up is sent.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="neg-max-followups">Max follow-ups</Label>
                <Input
                  id="neg-max-followups"
                  type="number"
                  min="0"
                  max="10"
                  step="1"
                  value={form.negotiation_max_followups}
                  onChange={(e) => setField("negotiation_max_followups", e.target.value)}
                  placeholder="2"
                />
                <p className="text-xs text-muted-foreground">
                  Maximum number of follow-ups per thread before it is abandoned.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="neg-min-interval">Min interval between sends (hours)</Label>
                <Input
                  id="neg-min-interval"
                  type="number"
                  min="1"
                  max="720"
                  step="1"
                  value={form.negotiation_min_interval_hours}
                  onChange={(e) => setField("negotiation_min_interval_hours", e.target.value)}
                  placeholder="24"
                />
                <p className="text-xs text-muted-foreground">
                  Minimum hours between any two outbound messages on a thread.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="neg-decline-cooldown">Decline cooldown (days)</Label>
                <Input
                  id="neg-decline-cooldown"
                  type="number"
                  min="0"
                  max="365"
                  step="1"
                  value={form.negotiation_decline_cooldown_days}
                  onChange={(e) => setField("negotiation_decline_cooldown_days", e.target.value)}
                  placeholder="30"
                />
                <p className="text-xs text-muted-foreground">
                  Days to wait before re-initiating with a vendor who declined.
                </p>
              </div>
            </div>
          </div>

          {/* Sender address section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Sending
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="neg-from-email">Send negotiations from</Label>
              <Input
                id="neg-from-email"
                type="email"
                value={form.negotiation_from_email}
                onChange={(e) => setField("negotiation_from_email", e.target.value)}
                placeholder="negotiate@yourdomain.com"
              />
              <p className="text-xs text-muted-foreground">
                From address used on outbound negotiation emails. Leave blank to use the default{" "}
                <span className="font-mono">negotiate@trydiscorun.com</span>. The domain must be
                verified in Resend before this takes effect.
              </p>
            </div>
          </div>

          {/* Tone & Voice section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Tone &amp; Voice
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="neg-tone">Default negotiation tone</Label>
                <Select
                  value={form.default_negotiation_tone}
                  onValueChange={(v) => setField("default_negotiation_tone", v)}
                >
                  <SelectTrigger id="neg-tone">
                    <SelectValue placeholder="Select tone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  AI tone pre-filled when starting a new negotiation or campaign.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="neg-default-target-discount">Default target discount (%)</Label>
                <Input
                  id="neg-default-target-discount"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.default_target_discount_pct}
                  onChange={(e) => setField("default_target_discount_pct", e.target.value)}
                  placeholder="e.g. 2.00"
                />
                <p className="text-xs text-muted-foreground">
                  Pre-fills the Target Discount % field when starting a negotiation.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="neg-default-days-early">Default early pay days</Label>
                <Input
                  id="neg-default-days-early"
                  type="number"
                  min="1"
                  max="365"
                  step="1"
                  value={form.default_target_days_early}
                  onChange={(e) => setField("default_target_days_early", e.target.value)}
                  placeholder="e.g. 20"
                />
                <p className="text-xs text-muted-foreground">
                  Pre-fills the Early Pay Days field when starting a negotiation.
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="neg-signature">Email signature / greeting</Label>
              <Textarea
                id="neg-signature"
                rows={3}
                value={form.negotiation_signature}
                onChange={(e) => setField("negotiation_signature", e.target.value)}
                placeholder="e.g. Best regards,&#10;The Finance Team at Acme Corp"
                className="resize-none text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Appended to AI-drafted negotiation emails. Leave blank to use none.
              </p>
            </div>
          </div>

          {/* Finance + AI section */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Finance &amp; AI
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="neg-cost-of-capital">Cost of capital APR (%)</Label>
                <Input
                  id="neg-cost-of-capital"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.cost_of_capital_apr}
                  onChange={(e) => setField("cost_of_capital_apr", e.target.value)}
                  placeholder="12"
                />
                <p className="text-xs text-muted-foreground">
                  Used as the default floor APR when suggesting negotiation targets.
                </p>
              </div>
            </div>
          </div>

          <Button type="submit" disabled={saving} className="w-full sm:w-auto">
            {saving ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                Saving…
              </>
            ) : (
              "Save Negotiation Settings"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
